import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { composeTest, errorDetail, fetchSubgroups, humanError } from '../api';
import { ConfirmPanel } from '../components/ConfirmPanel';
import { Icon } from '../components/icons';
import { WorkspaceShell } from '../components/WorkspaceShell';
import { withAdminOnly } from '../components/withAdminOnly';
import { skippedSummary } from '../model';
import type { ComposeResult } from '../model';
import { pushNotice, takeNotice } from '../notices';
import type { WorkspaceNotice } from '../notices';
import { clearPickedRows, clearSelection } from '../selectionStore';
import {
  clearDraft, hasContent, loadDraft, moveItem, removeItem, saveDraft, toComposeRequest,
} from '../wizardStore';
import type { WizardDraft, WizardNewItem } from '../wizardStore';
import { questionsLabel } from '../words';
import { StepName } from './wizard/StepName';
import { StepQuestions } from './wizard/StepQuestions';
import { StepReview } from './wizard/StepReview';

const STEPS = ['Название', 'Вопросы', 'Проверка'];

/** Всё, что мастер хранит в браузере, включая выбор на экране «Готовые вопросы». */
const forgetEverything = () => {
  clearDraft();
  clearSelection(0);
  clearPickedRows(0);
};

const TestWizardPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawStep = Number(searchParams.get('step'));
  const stepParam = Number.isInteger(rawStep) ? rawStep : 0; // «?step=2.5» и «?step=abc» считаются отсутствием шага

  const [initial] = useState(() => loadDraft());
  const [draft, setDraft] = useState<WizardDraft>(initial);
  const [askResume, setAskResume] = useState(() => !searchParams.has('step') && hasContent(initial));
  const [saved, setSaved] = useState<{ result: ComposeResult; published: boolean } | null>(null);
  const [notice, setNotice] = useState<WorkspaceNotice | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [subgroups, setSubgroups] = useState<string[]>([]);
  const [movedIndex, setMovedIndex] = useState<number | null>(null);
  const [leaving, setLeaving] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const urlFixed = useRef(false);

  useEffect(() => {
    // Не в инициализаторе useState: под <React.StrictMode> компонент рендерится дважды, второй вызов вернул бы null.
    const incoming = takeNotice();
    if (incoming) setNotice(incoming);
  }, []);

  const hasTitle = draft.title.trim() !== '';
  let step = stepParam >= 1 && stepParam <= 3 ? stepParam : 1;
  if (step > 1 && !hasTitle) step = 1;
  if (step === 3 && draft.items.length === 0) step = 2;

  useEffect(() => {
    saveDraft(draft);
  }, [draft]);

  useEffect(() => {
    let cancelled = false;
    fetchSubgroups()
      .then((list) => { if (!cancelled) setSubgroups(list); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (stepParam !== 0 && step !== stepParam) setSearchParams({ step: String(step) }, { replace: true });
  }, [step, stepParam, setSearchParams]);

  useEffect(() => {
    // Первый показ без ?step (или с неверным): записываем шаг в адрес, чтобы перезагрузка не спрашивала
    // «Продолжить или начать заново» про только что набранное. Только один раз: дальше адрес меняют кнопки мастера,
    // а после выбора «Продолжить» адрес на мгновение остаётся прежним (переход идёт отложенно).
    if (urlFixed.current) return;
    urlFixed.current = true;
    if (!askResume && stepParam === 0) setSearchParams({ step: String(step) }, { replace: true });
  }, [askResume, step, stepParam, setSearchParams]);

  const change = (patch: Partial<WizardDraft>) => setDraft((current) => ({ ...current, ...patch }));
  const goStep = (next: number) => setSearchParams({ step: String(next) });
  // Кнопки мастера: сообщение относится к шагу, который человек покидает, и на другом шаге его смысла нет
  // (например, «Он внизу, в списке…» на шаге 3, где списка нет). `save` зовёт `goStep` сам и свои сообщения оставляет.
  const moveTo = (next: number) => {
    setNotice(null);
    goStep(next);
  };

  const move = (index: number, delta: number) => {
    setDraft((current) => ({ ...current, items: moveItem(current.items, index, delta) }));
    setMovedIndex(index + delta);
  };
  const remove = (index: number) => {
    setDraft((current) => ({ ...current, items: removeItem(current.items, index) }));
    setMovedIndex(null);
  };
  const addManual = (item: WizardNewItem) => {
    setDraft((current) => ({ ...current, items: [...current.items, item] }));
    setMovedIndex(null);
    setNotice({ kind: 'ok', text: 'Вопрос добавлен в тест. Он внизу, в списке «Вопросы в этом тесте».' });
  };

  const discard = () => {
    forgetEverything();
    pushNotice({ kind: 'info', text: 'Создание теста отменено. Ничего не сохранилось.' });
    navigate('/admin/tests');
  };
  const leave = () => (draft.items.length > 0 ? setLeaving(true) : discard());

  const startOver = () => {
    forgetEverything();
    setDraft(loadDraft());
    setAskResume(false);
    setSearchParams({ step: '1' });
  };
  const resume = () => {
    setAskResume(false);
    setSearchParams({ step: hasTitle ? '2' : '1' });
  };

  const save = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const result = await composeTest(toComposeRequest(draft));
      forgetEverything();
      setNotice(null); // старое сообщение не должно доехать до итогового экрана
      setSaved({ result, published: draft.who === 'all' });
    } catch (error) {
      const info = errorDetail(error);
      if (info?.code === 'questions_not_found') {
        const missing = Array.isArray(info.missing_ids) ? (info.missing_ids as number[]) : [];
        setDraft((current) => ({
          ...current,
          items: current.items.filter((item) => item.kind !== 'source' || !missing.includes(item.id)),
        }));
        setNotice({
          kind: 'info',
          text: 'Некоторых готовых вопросов больше нет в системе. Мы убрали их из списка. Проверьте список и сохраните тест ещё раз.',
        });
        goStep(2);
      } else if (info?.code === 'test_would_be_empty') {
        setNotice({
          kind: 'info',
          text: 'Ни один вопрос не удалось добавить, поэтому тест не создан. Проверьте вопросы и попробуйте ещё раз.',
        });
      } else {
        setNotice({
          kind: 'info',
          text: humanError(error, 'Не получилось сохранить тест. Всё, что вы ввели, осталось на экране: попробуйте ещё раз.'),
        });
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  if (saved) {
    const { result, published } = saved;
    const testId = result.test.id;
    const extra = result.skipped.length > 0 ? ` Не все вопросы попали в тест. ${skippedSummary(result.skipped)}` : '';
    return (
      <WorkspaceShell notice={notice} onCloseNotice={() => setNotice(null)}>
        <section className="tw-screen">
          <div className="tw-card tw-done">
            <span className="tw-done-ico"><Icon name="check" size={44} large /></span>
            <h1>{`Тест «${result.test.title}» сохранён`}</h1>
            <p className="tw-lead">
              {(published
                ? 'Теперь его видят все сотрудники и могут пройти.'
                : 'Сейчас его не видит никто. Откройте его сотрудникам, когда будете готовы: всем или только выбранным людям.') + extra}
            </p>
            <div className="tw-row tw-btn-row" style={{ justifyContent: 'center' }}>
              <button type="button" className="tw-btn tw-btn-primary tw-btn-big" onClick={() => navigate(`/admin/tests/${testId}/assign`)}>
                Дать тест сотрудникам
              </button>
              <button type="button" className="tw-btn tw-btn-big" onClick={() => navigate(`/admin/tests/${testId}`)}>
                Открыть тест
              </button>
              <button type="button" className="tw-btn tw-btn-big" onClick={() => navigate('/admin/tests')}>
                К списку тестов
              </button>
            </div>
          </div>
        </section>
      </WorkspaceShell>
    );
  }

  if (askResume) {
    return (
      <WorkspaceShell notice={notice} onCloseNotice={() => setNotice(null)}>
        <section className="tw-screen">
          <h1>Создание теста</h1>
          <div className="tw-card tw-stack-sm">
            <h2>Есть неоконченный тест</h2>
            <p className="tw-help">
              {`Вы уже начали создавать тест${hasTitle ? ` «${draft.title.trim()}»` : ''}. Продолжить его или начать новый?`}
            </p>
            <div className="tw-row tw-btn-row">
              <button type="button" className="tw-btn tw-btn-primary" onClick={resume}>Продолжить</button>
              <button type="button" className="tw-btn" onClick={startOver}>Начать заново</button>
            </div>
          </div>
        </section>
      </WorkspaceShell>
    );
  }

  return (
    <WorkspaceShell notice={notice} onCloseNotice={() => setNotice(null)}>
      <section className="tw-screen">
        <div className="tw-row">
          <button type="button" className="tw-btn tw-btn-quiet" disabled={saving} onClick={leave}>
            <Icon name="left" />
            Выйти без сохранения
          </button>
        </div>
        {leaving && (
          <ConfirmPanel
            title="Выйти без сохранения?"
            text={`Всё, что вы собрали, пропадёт: ${questionsLabel(draft.items.length)}.`}
            confirmLabel="Да, выйти"
            cancelLabel="Нет, остаться"
            onConfirm={discard}
            onCancel={() => setLeaving(false)}
            busy={saving}
          />
        )}
        <div>
          <h1>Создание теста</h1>
          <p className="tw-lead" style={{ marginTop: 8 }}>Всего три шага. Можно вернуться назад — ничего не пропадёт.</p>
        </div>
        <div className="tw-steps" aria-label="Шаги создания теста">
          {STEPS.map((label, index) => {
            const number = index + 1;
            const done = step > number;
            const on = step === number;
            return (
              <div key={label} className={`tw-step${on ? ' tw-step-on' : ''}${done ? ' tw-step-done' : ''}`} aria-current={on ? 'step' : undefined}>
                <span className="tw-step-n">{done ? <Icon name="check" size={20} /> : <span>{number}</span>}</span>
                <span>{label}</span>
              </div>
            );
          })}
        </div>

        {step === 1 && (
          <StepName
            draft={draft}
            subgroups={subgroups}
            moreOpen={moreOpen}
            onToggleMore={() => setMoreOpen(!moreOpen)}
            onChange={change}
            onNext={() => moveTo(2)}
          />
        )}
        {step === 2 && (
          <div data-guide="wizard-questions"><StepQuestions
            draft={draft}
            movedIndex={movedIndex}
            onMove={move}
            onRemove={remove}
            onAddManual={addManual}
            onBack={() => moveTo(1)}
            onNext={() => moveTo(3)}
          /></div>
        )}
        {step === 3 && (
          <div data-guide="wizard-review"><StepReview draft={draft} saving={saving} onChange={change} onBack={() => moveTo(2)} onSave={() => void save()} /></div>
        )}
      </section>
    </WorkspaceShell>
  );
};

export default withAdminOnly(TestWizardPage);
